/**
 * Dark dropdown menu (black bg, white text) with keyboard and outside-click handling.
 * - Anchors to a provided ref and positions itself under the trigger
 * - Arrow keys cycle, Enter selects, Escape closes
 */
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Tooltip } from "@/components/atoms/primitives/Tooltip";

export interface DarkMenuItem {
  id: string;
  label: string;
  /** Sub-line under the label; the item's accessible description, never part of its name. */
  description?: string;
  disabled?: boolean;
  /**
   * De-emphasized but still selectable. Renders the label in a muted color
   * (vs `disabled`, which is non-interactive). Used for "already done, but you
   * can do it again" affordances like the LinkedIn import once a CSV import has
   * completed (LEV-925).
   */
  muted?: boolean;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** LEV-1090: render a visual divider ABOVE this item. */
  dividerAbove?: boolean;
  /**
   * LEV-1647: the item is an anonymous-gated control. It stays selectable (the
   * click opens the signup prompt), renders the muted treatment, marks itself
   * `data-gated="true"` for tests, and appends ", sign up to unlock" to its
   * accessible name. Pair with a lock `rightIcon` for the visual badge.
   */
  gated?: boolean;
  /** Native hover text on the item (e.g. what an unfamiliar source means). */
  title?: string;
  /**
   * Longer explanation in a Tooltip bubble on hover and keyboard focus, read
   * after `description` as the item's accessible description. Text only: the
   * bubble ignores the pointer and hides on leave, so a link inside it is
   * unreachable.
   */
  tooltip?: string;
}

type MenuItemButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  "data-dark-menu-item": string;
  "data-muted"?: string;
  "data-gated"?: string;
  /** The item's own description id (its sub-line). */
  ownDescribedBy?: string;
};

/**
 * Tooltip's cloneElement REPLACES the child's `aria-describedby` with the
 * bubble id, which would drop the sub-line from the accessible description.
 * Being a composite, this button receives the injected id as a prop and
 * merges it after its own, so screen readers hear sub-line then note.
 */
function MenuItemButton({
  ownDescribedBy,
  "aria-describedby": injected,
  ...rest
}: MenuItemButtonProps) {
  const describedBy = [ownDescribedBy, injected].filter(Boolean).join(" ") || undefined;
  return <button {...rest} aria-describedby={describedBy} />;
}

export function DarkMenu({
  items,
  open,
  onOpenChange,
  onSelect,
  anchorRef,
  align = "start",
  minWidth = 260,
  verticalPlacement = "below",
  portal = true,
  placement,
  variant = "dark",
}: {
  items: DarkMenuItem[];
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSelect: (id: string) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  align?: "start" | "end";
  minWidth?: number;
  verticalPlacement?: "below" | "center";
  /** When true, render into document.body using fixed positioning */
  portal?: boolean;
  /** Sugar for positioning the menu relative to the trigger */
  placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end" | "sidebar" | "sidebar-bottom";
  /** "light" = white bg, single border, slate text (for prospect header); "dark" = default black */
  variant?: "dark" | "light";
}) {
  const idBase = useId();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const focusIndexRef = useRef<number>(-1);
  const lastPosRef = useRef<{ left: number; top: number; transform?: string } | null>(
    null,
  );
  // Track whether we've mounted to avoid SSR/hydration mismatch with portals
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const enabledItems = useMemo(
    () => items.map((it, idx) => ({ ...it, idx })).filter((it) => !it.disabled),
    [items],
  );

  // Each open starts arrow navigation from the top; a stale index would also
  // make the off-item Enter fallback select whatever was arrowed last time.
  useEffect(() => {
    focusIndexRef.current = -1;
  }, [open]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      const inMenu = !!menuRef.current?.contains(t);
      const inAnchor = !!anchorRef.current?.contains(t);
      if (!inMenu && !inAnchor) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (enabledItems.length === 0) return;
        const dir = e.key === "ArrowDown" ? 1 : -1;
        let next = focusIndexRef.current;
        next = (next + dir + enabledItems.length) % enabledItems.length;
        focusIndexRef.current = next;
        const node = menuRef.current?.querySelector<HTMLElement>(
          `[data-dark-menu-item="${enabledItems[next].id}"]`,
        );
        node?.focus();
      }
      if (e.key === "Enter") {
        // The item with focus wins over the arrow index: Tab moves focus
        // without updating it. Enter on an item button also fires its native
        // click (a second onSelect), so it is cancelled: one Enter, one select.
        const target = e.target instanceof Element ? e.target : null;
        const focusedItem = target?.closest<HTMLElement>("[data-dark-menu-item]");
        if (focusedItem && menuRef.current?.contains(focusedItem)) {
          e.preventDefault();
          const id = focusedItem.dataset.darkMenuItem;
          if (id && enabledItems.some((it) => it.id === id)) onSelect(id);
          return;
        }
        // Focus is off the items (trigger, container): the arrow index, as before.
        const next = focusIndexRef.current;
        if (next >= 0 && next < enabledItems.length) {
          onSelect(enabledItems[next].id);
        }
      }
    };
    document.addEventListener("mousedown", onDocPointer, true); // capture phase
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, anchorRef, enabledItems, onOpenChange, onSelect]);

  // Initial static style (top/left updated in rAF below when open)
  const style: React.CSSProperties = useMemo(
    () => ({
      position: portal ? "fixed" : "absolute",
      minWidth,
      zIndex: 9999,
      display: open ? "block" : "none",
    }),
    [open, portal, minWidth],
  );

  // Imperatively position next to the anchor. We avoid React state updates so
  // panning the canvas doesn't cause component re-renders on every move.
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const el = menuRef.current;

    const compute = () => {
      const a = anchorRef.current;
      if (!el || !a) return;
      const rect = a.getBoundingClientRect();

      let left: number;
      let top: number;
      let transform: string | undefined;

      if (placement === "sidebar") {
        // 72px = 64px sidebar width + 8px gap, matching panel positioning
        left = 72;
        top = rect.top;
      } else if (placement === "sidebar-bottom") {
        left = 72;
        top = rect.bottom;
        transform = "translateY(-100%)";
      } else if (placement) {
        const isTop = placement.startsWith("top");
        const isEnd = placement.endsWith("end");
        left = isEnd ? rect.right - minWidth : rect.left;
        top = isTop ? rect.top - 8 : rect.bottom + 8;
        if (isTop) transform = "translateY(-100%)";
      } else {
        left = align === "end" ? rect.right - minWidth : rect.left;
        top =
          verticalPlacement === "center" ? rect.top + rect.height / 2 : rect.bottom + 8;
        transform = verticalPlacement === "center" ? "translateY(-50%)" : undefined;
      }

      // Convert to page coords when not using a portal
      const nextLeft = portal ? left : left + window.scrollX;
      const nextTop = portal ? top : top + window.scrollY;

      const prev = lastPosRef.current;
      if (
        !prev ||
        prev.left !== nextLeft ||
        prev.top !== nextTop ||
        prev.transform !== transform
      ) {
        el.style.left = `${nextLeft}px`;
        el.style.top = `${nextTop}px`;
        el.style.transform = transform ?? "";
        lastPosRef.current = { left: nextLeft, top: nextTop, transform };
      }
      raf = window.requestAnimationFrame(compute);
    };

    raf = window.requestAnimationFrame(compute);
    const onResize = () => {
      lastPosRef.current = null; // force recompute on next frame
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
  }, [open, anchorRef, align, placement, minWidth, verticalPlacement, portal]);

  const isLight = variant === "light";
  const wrapperClass = isLight
    ? "rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
    : "rounded-2xl border border-white/10 bg-[#1d1f24] p-1 text-white shadow-[0_28px_50px_rgba(15,16,20,0.3)]";
  const itemClass = isLight
    ? "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
    : "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-white/40";
  const itemDisabledClass = isLight
    ? "cursor-not-allowed text-slate-400"
    : "cursor-not-allowed text-white/40";
  const itemHoverClass = isLight
    ? "hover:bg-slate-100"
    : "hover:bg-white/10";
  // De-emphasized but still selectable (LEV-925). Muted text + hover affordance.
  const itemMutedClass = isLight
    ? "text-slate-400 hover:bg-slate-50"
    : "text-white/50 hover:bg-white/5";
  const descClass = isLight
    ? "truncate text-xs text-slate-500"
    : "truncate text-xs text-white/70";

  const element = (
    <div
      ref={menuRef}
      role="menu"
      className={wrapperClass}
      style={style}
      tabIndex={-1}
    >
      <div className={isLight ? "" : "rounded-2xl border border-white/10 bg-black p-2 text-white shadow-2xl ring-2 ring-white/5"}>
        {items.map((item) => {
            const disabled = !!item.disabled;
            const muted = !disabled && (!!item.muted || !!item.gated);
            const labelId = `${idBase}-${item.id}-label`;
            const descriptionId = `${idBase}-${item.id}-description`;
            const button = (
              <MenuItemButton
                data-dark-menu-item={item.id}
                data-muted={muted ? "true" : undefined}
                data-gated={item.gated ? "true" : undefined}
                aria-label={item.gated ? `${item.label}, sign up to unlock` : undefined}
                aria-labelledby={item.description && !item.gated ? labelId : undefined}
                ownDescribedBy={item.description ? descriptionId : undefined}
                title={item.title}
                type="button"
                role="menuitem"
                className={`w-full ${itemClass} ${
                  disabled
                    ? itemDisabledClass
                    : muted
                      ? itemMutedClass
                      : itemHoverClass
                }`}
                onClick={() => !disabled && onSelect(item.id)}
                disabled={disabled}
              >
                {item.icon ? (
                  <div className="flex-shrink-0">{item.icon}</div>
                ) : null}
                <div className="min-w-0 flex-1">
                  <div id={labelId} className="truncate font-medium">{item.label}</div>
                  {item.description && (
                    <div id={descriptionId} className={descClass}>
                      {item.description}
                    </div>
                  )}
                </div>
                {item.rightIcon ? (
                  <div className="flex-shrink-0">{item.rightIcon}</div>
                ) : null}
              </MenuItemButton>
            );
            return (
              <React.Fragment key={item.id}>
              {item.dividerAbove && (
                <div
                  className={
                    isLight
                      ? "my-1 border-t border-slate-200"
                      : "my-1 border-t border-white/10"
                  }
                  role="separator"
                />
              )}
              {item.tooltip ? (
                // Not portalled: z-tooltip (9000) sits below this menu's
                // z-index (9999), so a body-level bubble would render under
                // the menu. Inside the menu's stacking context it paints over
                // the neighboring items. align="end" keeps it within the
                // menu's right edge rather than past it.
                <Tooltip
                  label={item.tooltip}
                  position="bottom"
                  align="end"
                  multiline
                  delayedHover
                  className="flex w-full"
                >
                  {button}
                </Tooltip>
              ) : (
                button
              )}
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );

  // Only use portal after mount to avoid SSR/hydration mismatch
  if (portal && mounted) {
    return createPortal(element, document.body);
  }
  return element;
}

export default DarkMenu;
